import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { collection, getDocs, query, limit, orderBy, increment, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ChevronRight, Store, Calendar, Search, Clock, MapPin, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'sonner';

const HomePage = () => {
  const [banners, setBanners] = useState([]);
  const [locales, setLocales] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [localesRef, localesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [eventosRef, eventosInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ctaRef, ctaInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (banners.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [banners]);

  const fetchData = async () => {
    try {
      const bannersQuery = query(
        collection(db, 'banners'),
        orderBy('order', 'asc')
      );
      const bannersSnapshot = await getDocs(bannersQuery);
      const bannersData = bannersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const localesQuery = query(
        collection(db, 'locales'),
        limit(6)
      );
      const localesSnapshot = await getDocs(localesQuery);
      const localesData = localesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const eventosQuery = query(
        collection(db, 'eventos'),
        orderBy('fecha', 'asc'),
        limit(3)
      );
      const eventosSnapshot = await getDocs(eventosQuery);
      const eventosData = eventosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha?.toDate()
      }));

      setBanners(bannersData);
      setLocales(localesData);
      setEventos(eventosData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleLocalClick = async (localId) => {
    try {
      const localRef = doc(db, 'locales', localId);
      await updateDoc(localRef, {
        visitas: increment(1)
      });
    } catch (error) {
      console.error('Error updating visits:', error);
    }
  };

  const addToCalendar = (evento) => {
    const startDate = format(evento.fecha, "yyyyMMdd'T'HHmmss");
    const endDate = format(new Date(evento.fecha.getTime() + 2 * 60 * 60 * 1000), "yyyyMMdd'T'HHmmss");
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(evento.titulo)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(evento.descripcion)}&location=${encodeURIComponent(evento.ubicacion || 'Centro Comercial La Convención')}`;
    
    window.open(googleCalendarUrl, '_blank');
    toast.success('Evento añadido al calendario');
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <section ref={heroRef} className="relative h-[90vh] md:h-screen overflow-hidden">
        {banners.length === 0 ? (
          <div className="absolute inset-0 bg-gradient-to-br from-secondary via-primary to-accent flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center text-white px-4"
            >
              <h1 className="text-4xl md:text-6xl font-heading font-bold mb-4">
                Centro Comercial La Convención
              </h1>
              <p className="text-lg md:text-xl mb-8">
                Configure los banners desde el panel administrativo
              </p>
            </motion.div>
          </div>
        ) : (
          <>
            {banners.map((banner, index) => (
          <motion.div
            key={banner.id}
            initial={{ opacity: 0 }}
            animate={{
              opacity: currentSlide === index ? 1 : 0,
              scale: currentSlide === index ? 1 : 1.1
            }}
            transition={{ duration: 1 }}
            className="absolute inset-0"
            style={{ display: currentSlide === index ? 'block' : 'none' }}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${banner.imageUrl})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
            
            <div className="relative h-full flex items-center justify-center text-center px-4">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={currentSlide === index ? { y: 0, opacity: 1 } : { y: 50, opacity: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="max-w-4xl"
              >
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6">
                  {banner.title}
                </h1>
                <p className="text-lg md:text-xl lg:text-2xl text-white/90 mb-8">
                  {banner.subtitle}
                </p>
                <Link to="/locales">
                  <Button size="lg" className="bg-primary hover:bg-primary-glow text-primary-foreground px-8 py-6 text-lg">
                    Explorar Locales
                    <ChevronRight className="ml-2" size={20} />
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        ))}

        {banners.length > 0 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentSlide === index ? 'bg-primary w-8' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
        </>
        )}
      </section>

      <section ref={localesRef} className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={localesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4">
              Descubre Nuestros <span className="gradient-text">Locales</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explora una selección de nuestros mejores locales comerciales
            </p>
          </motion.div>

          {locales.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Store className="mb-4 text-muted-foreground" size={48} />
                <p className="text-lg font-medium mb-2">No hay locales disponibles</p>
                <p className="text-muted-foreground text-center">
                  Agrega locales desde el panel administrativo
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {locales.map((local, index) => (
              <motion.div
                key={local.id}
                initial={{ opacity: 0, y: 30 }}
                animate={localesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={`/local/${local.slug}`} onClick={() => handleLocalClick(local.id)}>
                  <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={local.imagenPrincipal}
                        alt={local.nombre}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4">
                        <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                          {local.categoria}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-6 flex-grow flex flex-col">
                      <h3 className="text-xl font-heading font-semibold mb-2 group-hover:text-primary transition-colors">
                        {local.nombre}
                      </h3>
                      <p className="text-muted-foreground mb-4 flex-grow">
                        {local.descripcion}
                      </p>
                      <Button variant="ghost" className="w-full mt-auto justify-between group-hover:text-primary">
                        Ver más
                        <ChevronRight size={18} />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={localesInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.8 }}
              className="text-center mt-12"
            >
              <Link to="/locales">
                <Button size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  Ver Todos los Locales
                  <Store className="ml-2" size={20} />
                </Button>
              </Link>
            </motion.div>
          </>
          )}
        </div>
      </section>

      <section ref={eventosRef} className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={eventosInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-4">
              Próximos <span className="gradient-text">Eventos</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              No te pierdas nuestras actividades y celebraciones especiales
            </p>
          </motion.div>

          {eventos.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="mb-4 text-muted-foreground" size={48} />
                <p className="text-lg font-medium mb-2">No hay eventos próximos</p>
                <p className="text-muted-foreground text-center">
                  Vuelve pronto para ver nuestros próximos eventos
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {eventos.map((evento, index) => (
                  <motion.div
                    key={evento.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={eventosInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={evento.imagen}
                          alt={evento.titulo}
                          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg">
                          <div className="text-2xl font-bold">
                            {format(evento.fecha, 'd', { locale: es })}
                          </div>
                          <div className="text-sm">
                            {format(evento.fecha, 'MMM', { locale: es })}
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-6 flex-grow flex flex-col">
                        <h3 className="text-xl font-heading font-semibold mb-3">
                          {evento.titulo}
                        </h3>
                        <p className="text-muted-foreground mb-4 flex-grow line-clamp-2">
                          {evento.descripcion}
                        </p>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock size={16} />
                            <span>
                              {format(evento.fecha, "EEEE, d 'de' MMMM 'a las' HH:mm", { locale: es })}
                            </span>
                          </div>
                          {evento.ubicacion && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <MapPin size={16} />
                              <span>{evento.ubicacion}</span>
                            </div>
                          )}
                        </div>

                        <Button
                          onClick={() => addToCalendar(evento)}
                          variant="outline"
                          className="w-full mt-auto border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                        >
                          <Plus className="mr-2" size={18} />
                          Agregar al Calendario
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={eventosInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.8 }}
                className="text-center mt-12"
              >
                <Link to="/eventos">
                  <Button size="lg" variant="outline" className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground">
                    Ver Todos los Eventos
                    <Calendar className="ml-2" size={20} />
                  </Button>
                </Link>
              </motion.div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;